// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import toggleFiltersOpen from "@eisinc/App/Handlers/Pages/InvoicePaymentHistory/ToggleFiltersOpen";
import { filterHeading } from "@eisinc/App/SharedStyles/GeneralStyles";
import { InvoicePaymentHistoryDataViewContext } from "@eisinc/App/Store/Selectors/InvoicePaymentHistorySelector";
import { pluralize } from "@eisinc/XDL/Utilities/string";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Filter from "@insite/mobius/Icons/Filter";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    toggleFiltersOpen,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryHeaderStyles {
    container?: GridContainerProps;
    heading?: TypographyProps;
    invoiceCountGridItem?: GridItemProps;
    totalText?: TypographyProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
}

export const headerStyles: InvoiceHistoryHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 15px;
        `,
    },
    heading: { variant: "h1" },
    invoiceCountGridItem: {
        width: 8,
    },
    totalText: {
        size: 20,
        lineHeight: "24px",
    },
    toggleFilterGridItem: {
        width: 4,
        style: { justifyContent: "flex-end" },
    },
    toggleFilterButton: {
        color: "common.background",
        shape: "pill",
        buttonType: "solid",
        shadow: false,
        css: css`
            padding: 0;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
};

const styles = headerStyles;

const InvoicePaymentHistoryHeader = (props: Props) => {
    const invoicePaymentHistoryDataView = useContext(InvoicePaymentHistoryDataViewContext);
    const invoicesCount =
        invoicePaymentHistoryDataView.value && invoicePaymentHistoryDataView.pagination
            ? invoicePaymentHistoryDataView.pagination.totalItemCount
            : 0;

    return (
        <>
            <Typography {...styles.heading}>{translate("My Past Payments")}</Typography>
            <GridContainer {...styles.container}>
                <GridItem {...styles.invoiceCountGridItem}>
                    <Typography {...styles.totalText}>
                        {pluralize(invoicesCount, "Invoice", { showNumber: true })}
                    </Typography>
                </GridItem>
                <GridItem {...styles.toggleFilterGridItem}>
                    <Clickable
                        {...styles.toggleFilterButton}
                        onClick={props.toggleFiltersOpen}
                        data-test-selector="invoicePaymentHistory_toggleFilter"
                    >
                        <Typography {...filterHeading}>{translate("Search & Filter")}</Typography>
                        <ButtonIcon {...styles.toggleFilterButtonIcon} />
                    </Clickable>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(InvoicePaymentHistoryHeader),
    definition: {
        group: "Invoice Payment History" as any,
        displayName: "Page Header",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
